import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faSquareXmark } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import './OpenItemsDialog.css';
import { SquiggleItem } from './types/types';
import { useOpenItemsContext } from './context/OpenItemsContext';

function OpenItemsDialog({
  openItem,
  close,
  closeItem
} : {
  openItem: (itemId: string) => void,
  close: (lastFocusedItemId: string|null) => void,
  closeItem: (itemId: string) => void
}) {
  const [filterString, setFilterString] = useState<string>('');
  const { itemsOpen, selectedItemId, setSelectedItemId } = useOpenItemsContext();
  const [selectedIndex, setSelectedIndex] = useState<number>(0);
  const [lastFocusedItemId, setLastFocusedItemId] = useState<string|null>(selectedItemId);

  const sortedOpenItems = useMemo(() => (
    [...itemsOpen].sort((a, b) => a.name.localeCompare(b.name))
  ), [itemsOpen]);

  const filteredItems = useMemo(() => {
    const trimmedFilter = filterString.trim().toLowerCase();
    if (trimmedFilter === '') {
      return sortedOpenItems;
    }
    return sortedOpenItems.filter((item: SquiggleItem) =>
      item.name.toLowerCase().includes(trimmedFilter)
    );
  }, [sortedOpenItems, filterString]);

  useEffect(() => {
    setSelectedItemId(null);
  }, [setSelectedItemId]);

  useEffect(() => {
    setSelectedIndex(filteredItems.length > 0 ? 0 : -1);
  }, [filterString, filteredItems.length]);

  const focusItem = useCallback((itemId: string) => {
    setLastFocusedItemId(itemId);
    setSelectedItemId(itemId);
    openItem(itemId);
    close(itemId);
  }, [close, setSelectedItemId, openItem, setLastFocusedItemId]);

  const handleFilterChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setFilterString(e.target.value);
  }, [setFilterString]);

  const handleCloseItem = useCallback((itemId: string) => {
    closeItem(itemId);
    setLastFocusedItemId((lastFocused) => (lastFocused === itemId ? null : lastFocused));
  }, [closeItem]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const targetIndex = selectedIndex >= 0 ? selectedIndex : 0;
      const targetItem = filteredItems[targetIndex];
      if (targetItem) {
        focusItem(targetItem.id);
      }
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowDown') {
      setSelectedIndex((prevIndex) => Math.min(prevIndex + 1, filteredItems.length - 1));
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'ArrowUp') {
      setSelectedIndex((prevIndex) => Math.max(prevIndex - 1, 0));
      e.stopPropagation();
      e.preventDefault();
      return;
    }

    if (e.key === 'Escape') {
      close(lastFocusedItemId);
      e.stopPropagation();
      e.preventDefault();
    }
  }, [close, filteredItems, focusItem, lastFocusedItemId, selectedIndex]);

  return <div className="openitemsbackdrop" onClick={() => close(lastFocusedItemId)}>
    <div className="openitems" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
      <div className="openitemsbox">
        <input
          type="text"
          value={filterString}
          placeholder="Filter open items..."
          autoFocus
          onChange={handleFilterChange}
          onKeyDown={handleKeyDown}>
        </input>
      </div>
      <div className="openitemsresults">
        {
          filteredItems.length === 0
            ? <div className="openitemsitem noresults">No open items</div>
            : filteredItems.map((item, index) =>
              <div
                key={item.id}
                className={
                  "openitemsitem " +
                  item.type +
                  (index === selectedIndex ? " selected" : "")
                }
                onClick={() => focusItem(item.id)}>
                <div className="resultname">{item.name}</div>
                <button
                  className="closerow"
                  type="button"
                  title="Close item"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCloseItem(item.id);
                  }}>
                  <FontAwesomeIcon icon={faSquareXmark as IconProp} />
                </button>
              </div>
            )
        }
      </div>
    </div>
  </div>;
}

export default OpenItemsDialog;